/**
 * Radar Export — PowerPoint (.pptx) and PDF (.pdf) for Technology Radar.
 *
 * Corporate Design: dark #272727 background, Big John headings,
 * diagonal orange stripe (Batemo Styleguide), branded footer.
 */

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** Get a branded PNG data URL from an ECharts instance. */
function getChartImageWithBranding(chartInstance) {
  chartInstance.setOption({
    graphic: [
      {
        type: 'text', id: '_pptBrandLogo', right: 14, bottom: 20,
        style: { text: 'BATEMO', fill: 'rgba(255,255,255,0.25)', font: 'bold 12px "Big John", sans-serif' },
        silent: true,
      },
      {
        type: 'text', id: '_pptBrandUrl', left: 14, bottom: 20,
        style: { text: 'insights.batemo.com', fill: 'rgba(255,255,255,0.25)', font: '10px Roboto, sans-serif' },
        silent: true,
      },
    ],
  })

  const dataUrl = chartInstance.getDataURL({
    type: 'png', pixelRatio: 3, backgroundColor: '#272727', excludeComponents: ['toolbox'],
  })

  chartInstance.setOption({
    graphic: [{ id: '_pptBrandLogo', $action: 'remove' }, { id: '_pptBrandUrl', $action: 'remove' }],
  })

  return dataUrl
}

/** Extract plain text from JSX. */
export function extractText(jsx) {
  if (jsx == null) return ''
  if (typeof jsx === 'string') return jsx
  if (typeof jsx === 'number') return String(jsx)
  if (Array.isArray(jsx)) return jsx.map(extractText).join('')
  if (jsx.props) {
    const children = jsx.props.children
    if (Array.isArray(children)) return children.map(extractText).join('')
    return extractText(children)
  }
  return ''
}

export function getChartImagesFromInstances(instances) {
  return instances.filter(Boolean).map(getChartImageWithBranding)
}

// ---------------------------------------------------------------------------
// PPTX helpers — diagonal orange stripe (Corporate Design)
// ---------------------------------------------------------------------------

// pptxgenjs custom shape points use percentages (0-100) of the shape bounding box.
// The diagonal stripe goes from bottom-left to slightly above on the right.

/** Add diagonal orange stripe at the bottom of a slide (like Batemo template). */
function addDiagonalStripeBottom(slide, color = 'ea5b0c') {
  // Trapezoid: left side taller, right side starts higher = diagonal top edge
  // Shape bounding box: full width, bottom portion of slide
  slide.addShape('custGeom', {
    x: 0, y: 4.4, w: 10, h: 1.23,
    fill: { color },
    line: { type: 'none' },
    custGeom: [
      // Points as percentage of bounding box (w=10, h=1.23)
      { x: 0, y: 40 },      // top-left (starts ~40% down = diagonal start)
      { x: 100, y: 0 },     // top-right (starts at top = higher)
      { x: 100, y: 100 },   // bottom-right
      { x: 0, y: 100 },     // bottom-left
    ],
  })
}

/** Add diagonal orange stripe at top of a slide (inverted, for transitions). */
function addDiagonalStripeTop(slide, color = 'ea5b0c') {
  slide.addShape('custGeom', {
    x: 0, y: 0, w: 10, h: 0.8,
    fill: { color },
    line: { type: 'none' },
    custGeom: [
      { x: 0, y: 0 },       // top-left
      { x: 100, y: 0 },     // top-right
      { x: 100, y: 60 },    // bottom-right (shorter)
      { x: 0, y: 100 },     // bottom-left (taller = diagonal)
    ],
  })
}

/** Standard footer: Batemo logo left + URL right */
function addFooter(slide) {
  slide.addImage({ path: '/logo/batemo_logo_white.svg', x: 0.3, y: 5.05, w: 1.2, h: 0.3 })
  slide.addText('insights.batemo.com', {
    x: 7.8, y: 5.15, w: 2.0, h: 0.2,
    fontSize: 7, fontFace: 'Roboto', color: '64748b', align: 'right',
  })
}

// ---------------------------------------------------------------------------
// PPTX — Content slide builder
// ---------------------------------------------------------------------------

function addContentSlide(pptx, { sectionTitle, slideTitle, insightText, chartImages, accentColor, slideNum, totalSlides }) {
  const s = pptx.addSlide()
  s.background = { color: '272727' }

  // Slide number (top-right) — only if multiple slides
  if (totalSlides > 1) {
    s.addText(`${slideNum} / ${totalSlides}`, {
      x: 9.0, y: 0.12, w: 0.8, h: 0.25,
      fontSize: 7, fontFace: 'Roboto', color: '64748b', align: 'right',
    })
  }

  // Section label — WHITE
  s.addText(sectionTitle.toUpperCase(), {
    x: 0.4, y: 0.12, w: 6, h: 0.35,
    fontSize: 13, fontFace: 'Big John', color: 'f1f5f9',
  })

  // Charts — 1200x700 hidden container = 1.71:1 aspect ratio
  const chartCount = chartImages.length
  const aspect = 1.71

  if (chartCount === 1) {
    const cw = 9.2
    const ch = cw / aspect
    s.addImage({ data: chartImages[0], x: 0.4, y: 0.55, w: cw, h: ch })
  } else if (chartCount >= 2) {
    const cw = 4.55
    const ch = cw / aspect
    s.addImage({ data: chartImages[0], x: 0.15, y: 0.55, w: cw, h: ch })
    s.addImage({ data: chartImages[1], x: 5.05, y: 0.55, w: cw, h: ch })
  }

  // Key Insight
  const insightY = 3.35
  s.addText(slideTitle, {
    x: 0.4, y: insightY, w: 9.2, h: 0.3,
    fontSize: 11, fontFace: 'Big John', color: accentColor || 'ea5b0c',
  })
  if (insightText) {
    s.addText(insightText, {
      x: 0.4, y: insightY + 0.35, w: 9.2, h: 0.8,
      fontSize: 8, fontFace: 'Roboto', color: 'cbd5e1', valign: 'top', wrap: true,
    })
  }

  addFooter(s)
}

// ---------------------------------------------------------------------------
// PPTX — Single slide export
// ---------------------------------------------------------------------------

export async function exportSlideToPptx({ chartInstances, slideTitle, insightJsx, sectionTitle, fileName }) {
  const PptxGenJS = (await import('pptxgenjs')).default
  const pptx = new PptxGenJS()
  pptx.layout = 'LAYOUT_16x9'
  pptx.author = 'Batemo Insights'
  pptx.company = 'Batemo GmbH'

  const chartImages = chartInstances.map(getChartImageWithBranding)
  addContentSlide(pptx, {
    sectionTitle, slideTitle, insightText: extractText(insightJsx),
    chartImages, accentColor: 'ea5b0c', slideNum: 1, totalSlides: 1,
  })

  await pptx.writeFile({ fileName: `${fileName}.pptx` })
}

// ---------------------------------------------------------------------------
// PPTX — Full section export (cover + content + end)
// ---------------------------------------------------------------------------

export async function exportSectionToPptx({ sectionTitle, sectionDescription, accentColor = 'ea5b0c', slides, fileName }) {
  const PptxGenJS = (await import('pptxgenjs')).default
  const pptx = new PptxGenJS()
  pptx.layout = 'LAYOUT_16x9'
  pptx.author = 'Batemo Insights'
  pptx.company = 'Batemo GmbH'
  pptx.subject = sectionTitle

  const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long' })

  // ===== COVER SLIDE =====
  const cover = pptx.addSlide()
  cover.background = { color: '272727' }

  cover.addImage({ path: '/logo/batemo_logo_white.svg', x: 3.0, y: 1.0, w: 4.0, h: 1.0 })

  cover.addText('TECHNOLOGY RADAR', {
    x: 0, y: 2.3, w: 10, h: 0.35,
    fontSize: 11, fontFace: 'Roboto', color: '94a3b8', align: 'center',
  })

  cover.addText(sectionTitle.toUpperCase(), {
    x: 0, y: 2.7, w: 10, h: 0.6,
    fontSize: 24, fontFace: 'Big John', color: 'f1f5f9', align: 'center',
  })

  if (sectionDescription) {
    cover.addText(sectionDescription, {
      x: 1.5, y: 3.4, w: 7, h: 0.6,
      fontSize: 10, fontFace: 'Roboto', color: '94a3b8', align: 'center', wrap: true,
    })
  }

  // "Batemo Insights" + date at bottom
  cover.addText('Batemo Insights', {
    x: 0.4, y: 4.85, w: 3, h: 0.2,
    fontSize: 8, fontFace: 'Roboto', color: '94a3b8',
  })
  cover.addText(dateStr, {
    x: 7.5, y: 4.85, w: 2.3, h: 0.2,
    fontSize: 8, fontFace: 'Roboto', color: '94a3b8', align: 'right',
  })

  // Diagonal orange stripe at bottom
  addDiagonalStripeBottom(cover, accentColor)

  // ===== CONTENT SLIDES =====
  slides.forEach((slideData, idx) => {
    addContentSlide(pptx, {
      sectionTitle,
      slideTitle: slideData.slideTitle,
      insightText: slideData.insightText,
      chartImages: slideData.chartImages,
      accentColor,
      slideNum: idx + 1,
      totalSlides: slides.length,
    })
  })

  // ===== END SLIDE =====
  const end = pptx.addSlide()
  end.background = { color: '272727' }

  end.addImage({ path: '/logo/batemo_logo_white.svg', x: 3.0, y: 1.2, w: 4.0, h: 1.0 })

  end.addText('Explore more at', {
    x: 0, y: 2.5, w: 10, h: 0.3,
    fontSize: 10, fontFace: 'Roboto', color: '94a3b8', align: 'center',
  })
  end.addText('insights.batemo.com', {
    x: 0, y: 2.8, w: 10, h: 0.5,
    fontSize: 18, fontFace: 'Big John', color: 'ea5b0c', align: 'center',
  })

  const year = new Date().getFullYear()
  end.addText(`\u00A9 ${year} Batemo GmbH`, {
    x: 0.4, y: 4.85, w: 3, h: 0.2,
    fontSize: 7, fontFace: 'Roboto', color: '64748b',
  })
  end.addText(dateStr, {
    x: 7.5, y: 4.85, w: 2.3, h: 0.2,
    fontSize: 7, fontFace: 'Roboto', color: '64748b', align: 'right',
  })

  // Diagonal orange stripe at bottom
  addDiagonalStripeBottom(end, accentColor)

  await pptx.writeFile({ fileName: `${fileName}.pptx` })
}

// ---------------------------------------------------------------------------
// PDF Export — branded one-pager
// ---------------------------------------------------------------------------

async function loadFont(url) {
  const res = await fetch(url)
  return await res.arrayBuffer()
}

export async function exportSlideToPdf({ chartInstances, slideTitle, insightJsx, sectionTitle, fileName }) {
  const { jsPDF } = await import('jspdf')

  const W = 10, H = 5.625
  const doc = new jsPDF({ orientation: 'landscape', unit: 'in', format: [W, H] })

  try {
    const [bigJohnBuf, robotoBuf] = await Promise.all([
      loadFont('/fonts/big_john.ttf'), loadFont('/fonts/Roboto-Regular.ttf'),
    ])
    doc.addFileToVFS('big_john.ttf', new Uint8Array(bigJohnBuf))
    doc.addFont('big_john.ttf', 'BigJohn', 'normal')
    doc.addFileToVFS('Roboto-Regular.ttf', new Uint8Array(robotoBuf))
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal')
  } catch (e) {
    console.warn('[PDF] Font loading failed:', e)
  }

  doc.setFillColor(39, 39, 39)
  doc.rect(0, 0, W, H, 'F')

  doc.setFillColor(234, 91, 12)
  doc.rect(0, 0, W, 0.02, 'F')

  doc.setFont('BigJohn', 'normal')
  doc.setFontSize(14)
  doc.setTextColor(241, 245, 249)
  doc.text(sectionTitle.toUpperCase(), 0.4, 0.42)

  doc.setFontSize(12)
  doc.setTextColor(255, 255, 255)
  doc.text('BATEMO', W - 0.4, 0.42, { align: 'right' })

  const chartImages = chartInstances.map(getChartImageWithBranding)
  if (chartImages.length === 1) {
    doc.addImage(chartImages[0], 'PNG', 0.4, 0.65, 9.2, 9.2 / 1.71)
  } else if (chartImages.length >= 2) {
    const ch = 4.5 / 1.71
    doc.addImage(chartImages[0], 'PNG', 0.2, 0.65, 4.5, ch)
    doc.addImage(chartImages[1], 'PNG', 5.1, 0.65, 4.5, ch)
  }

  doc.setDrawColor(234, 91, 12)
  doc.setLineWidth(0.008)
  doc.line(0.4, 4.25, 3.5, 4.25)

  doc.setFont('BigJohn', 'normal')
  doc.setFontSize(11)
  doc.setTextColor(234, 91, 12)
  doc.text(slideTitle, 0.4, 4.48)

  const insightText = extractText(insightJsx)
  if (insightText) {
    doc.setFont('Roboto', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(203, 213, 225)
    doc.text(insightText, 0.4, 4.68, { maxWidth: 8.5 })
  }

  doc.setFillColor(234, 91, 12)
  doc.rect(0, H - 0.06, W, 0.06, 'F')

  doc.setFont('Roboto', 'normal')
  doc.setFontSize(6)
  doc.setTextColor(100, 116, 139)
  doc.text(`\u00A9 ${new Date().getFullYear()} Batemo GmbH`, 0.4, H - 0.12)
  doc.setFontSize(7)
  doc.setTextColor(148, 163, 184)
  doc.text('insights.batemo.com', W - 0.4, H - 0.12, { align: 'right' })

  doc.save(`${fileName}.pdf`)
}
